// Typed fetch wrapper for the miniapp-api BFF. Attaches the Bearer token and
// the X-Chat-Id header (required by chat-scoped routes, which also match it
// against the :chatId path param), and parses `{ error: { code, message } }`
// envelopes into ApiError.
import { getSessionChatId, getToken } from "../auth/session";
import { API_BASE } from "./base";
import { ApiError } from "./errors";
import type { CoreErrorBody } from "./types";

interface RequestOptions {
  method?: "GET" | "POST";
  /** Parsed JSON body for POST requests. */
  body?: unknown;
  /** Overrides the session chatId for the X-Chat-Id header (rarely needed). */
  chatId?: number;
  /** Send the X-Chat-Id header (default true). Auth verify does not need it. */
  withChatId?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, withChatId = true, signal } = opts;

  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const chatId = opts.chatId ?? getSessionChatId();
  if (withChatId && chatId !== undefined) headers["X-Chat-Id"] = String(chatId);

  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    const errBody = (await res.json().catch(() => null)) as CoreErrorBody | null;
    const code = errBody?.error?.code ?? "internal_error";
    throw new ApiError(code, errBody?.error?.message ?? res.statusText, res.status);
  }

  // 204-safe: some mutations could return empty bodies.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Path prefix for chat-scoped routes: /chats/:chatId/... */
function chatPath(suffix: string): string {
  const chatId = getSessionChatId();
  return `/chats/${chatId ?? ""}${suffix}`;
}

export const api = {
  request,
  chatPath,
};
